import React, { useState, useEffect } from 'react';
import { ArrowRight, ChevronDown, Sparkles } from 'lucide-react';
import { HeroArchitecture } from './HeroArchitecture';
import { SUMMIT_CONFIG } from '../config/summitConfig';

interface HeroProps {
  reducedMotion: boolean;
  onExploreCommittees: () => void;
  onRegisterNow: () => void;
}

export const Hero: React.FC<HeroProps> = ({
  reducedMotion,
  onExploreCommittees,
  onRegisterNow,
}) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  // Trigger sequence entrance animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  // Mouse parallax tracking
  useEffect(() => {
    if (reducedMotion) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Normalized between -1 and 1 from center
      const normX = (e.clientX / window.innerWidth - 0.5) * 2;
      const normY = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePos({ x: normX, y: normY });
    };

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowH = window.innerHeight;
      const progress = Math.min(Math.max(scrollY / windowH, 0), 1);
      setScrollProgress(progress);
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [reducedMotion]);

  // Typography subtle mouse offset (1-3px)
  const textOffsetX = reducedMotion ? 0 : mousePos.x * 3;
  const textOffsetY = reducedMotion ? 0 : mousePos.y * 3;

  // Scroll camera forward translation
  const heroZoomScale = reducedMotion ? 1 : 1 + scrollProgress * 0.12;
  const heroTranslateZ = reducedMotion ? 0 : -scrollProgress * 150;
  const heroOpacity = reducedMotion ? 1 : Math.max(1 - scrollProgress * 1.2, 0);

  return (
    <section 
      id="home"
      className="relative min-h-screen w-full flex flex-col justify-center items-center pt-24 pb-16 overflow-hidden perspective-stage"
      style={{
        perspective: '1400px',
      }}
    >
      {/* Background Subtle Marble Grid & Lighting */}
      <div 
        className="absolute inset-0 pointer-events-none transition-opacity duration-1000"
        style={{
          transform: `translate3d(${mousePos.x * 2}px, ${mousePos.y * 2}px, -300px)`,
          opacity: isLoaded ? 0.8 : 0,
        }}
      >
        <div className="absolute inset-0 bg-marble-vein" />
        <div className="absolute top-1/4 left-10 w-96 h-96 bg-beige/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-bronze/5 rounded-full blur-3xl" />
      </div>

      {/* Main Hero Container with Depth Transform */}
      <div 
        className="w-full px-6 sm:px-10 md:px-14 lg:px-16 xl:px-24 2xl:px-32 relative z-10 my-auto transition-all duration-300 preserve-3d"
        style={{
          transform: `scale(${heroZoomScale}) translateZ(${heroTranslateZ}px)`,
          opacity: heroOpacity,
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 xl:gap-16 items-center w-full">
          
          {/* =========================================================================
              LEFT COLUMN: Monumental Editorial Typography & Actions (Cols 1-6)
             ========================================================================= */}
          <div 
            className="lg:col-span-7 xl:col-span-6 flex flex-col justify-center text-left"
            style={{
              transform: `translate3d(${textOffsetX}px, ${textOffsetY}px, 20px)`,
              transition: 'transform 0.1s ease-out'
            }}
          >
            {/* Small Architectural Sub-Header with Bold Dates */}
            <div 
              className={`inline-flex flex-wrap items-center gap-2 px-3.5 py-1.5 border border-bronze/50 bg-charcoal-subtle/80 rounded-full w-fit mb-6 transition-all duration-700 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-bronze animate-pulse" />
              <span className="text-[10px] tracking-architectural uppercase text-beige font-semibold">
                FLSxMUN 2.0 •
              </span>
              <strong className="text-[11px] font-bold text-ivory tracking-widest uppercase bg-beige/10 px-2.5 py-0.5 rounded border border-beige/40">
                19th & 20th DECEMBER
              </strong>
            </div>

            {/* Cinematic Main Title */}
            <div className="space-y-1 mb-6">
              <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight text-ivory leading-[0.92]">
                <span 
                  className={`block transition-all duration-700 delay-100 ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                >
                  FUTURE
                </span>
                <span 
                  className={`block text-beige transition-all duration-700 delay-200 ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                >
                  LEADERS
                </span>
                <span 
                  className={`block transition-all duration-700 delay-300 ${
                    isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                >
                  SUMMIT
                </span>
              </h1>
            </div>

            {/* Tagline */}
            <div 
              className={`border-l-2 border-bronze pl-4 py-1 mb-6 transition-all duration-700 delay-400 ${
                isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
              }`}
            >
              <h2 className="font-serif text-xl sm:text-2xl md:text-3xl tracking-widest text-beige-300 font-light uppercase">
                FOR THE YOUTH, <br className="hidden sm:inline" />
                BY THE YOUTH.
              </h2>
            </div>

            {/* Supporting Text */}
            <p 
              className={`max-w-xl text-sm sm:text-base text-ivory/70 font-light leading-relaxed mb-8 transition-all duration-700 delay-500 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {SUMMIT_CONFIG.heroSupportingText}
            </p>

            {/* Action Buttons */}
            <div 
              className={`flex flex-col sm:flex-row items-stretch sm:items-center gap-4 transition-all duration-700 delay-600 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {/* Primary: REGISTER NOW */}
              <button
                onClick={onRegisterNow}
                className="group relative px-8 py-4 bg-gradient-to-r from-beige-300 via-beige to-beige-400 text-deepblack font-semibold text-xs tracking-architectural uppercase rounded-sm border border-beige shadow-xl hover:shadow-glow-bronze transition-all duration-300 transform hover:-translate-y-1 active:translate-y-0 flex items-center justify-center gap-3 overflow-hidden"
                data-cursor-interactive
                data-cursor-text="JOIN"
              >
                <span className="relative z-10">REGISTER NOW</span>
                <ArrowRight className="w-4 h-4 relative z-10 transition-transform duration-300 group-hover:translate-x-1.5" />
                {/* Subtle Sheen Effect */}
                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              </button>

              {/* Secondary: EXPLORE COMMITTEES */}
              <button
                onClick={onExploreCommittees}
                className="group px-7 py-4 border border-bronze/50 hover:border-beige bg-deepblack/60 hover:bg-charcoal-subtle/80 text-ivory text-xs tracking-architectural uppercase rounded-sm transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                data-cursor-interactive
                data-cursor-text="EXPLORE"
              >
                <span>EXPLORE COMMITTEES</span>
                <span className="text-bronze group-hover:text-beige transition-colors">→</span>
              </button>
            </div>

            {/* Editorial Summit Badges */}
            <div 
              className={`mt-10 pt-6 border-t border-white/10 flex items-center gap-6 sm:gap-10 text-[11px] text-ivory/50 uppercase tracking-widest transition-all duration-700 delay-700 ${
                isLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-bronze font-mono font-bold">09</span>
                <span>Chambers</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-bronze/40" />
              <div className="flex items-center gap-2">
                <strong className="text-beige font-mono font-bold text-xs tracking-wider">19-20 DECEMBER 2026</strong>
              </div>
            </div>

          </div>

          {/* =========================================================================
              RIGHT COLUMN: Monumental Greek Architecture (Cols 8-12)
             ========================================================================= */}
          <div 
            className={`lg:col-span-5 xl:col-span-6 flex items-center justify-center transition-all duration-1000 delay-300 ${
              isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <HeroArchitecture 
              mouseX={mousePos.x} 
              mouseY={mousePos.y} 
              scrollProgress={scrollProgress}
            />
          </div>

        </div>
      </div>

      {/* Bottom Scroll Indicator */}
      <div 
        onClick={onExploreCommittees}
        className={`absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer transition-all duration-700 delay-1000 ${
          isLoaded ? 'opacity-70 hover:opacity-100' : 'opacity-0'
        }`}
        data-cursor-interactive
        data-cursor-text="SCROLL"
      >
        <span className="text-[10px] tracking-widest uppercase text-beige/70 font-mono">
          SCROLL TO EXPLORE
        </span>
        <ChevronDown className="w-4 h-4 text-bronze animate-bounce" />
      </div>

      {/* Bottom Greek Key Separator */}
      <div className="absolute bottom-0 left-0 right-0 h-2 greek-key-border opacity-30" />
    </section>
  );
};
